import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Button from "../ui/Button";
import { cn } from "../../lib/cn";

export interface GuestHubHeroProps {
  /** Wired to the same batch-upload opener the Hub uses for browse/drop. */
  onBrowse?: () => void;
  /** True while a drag-over is in progress — highlights the drop zone. */
  isDragging?: boolean;
}

const FORMATS = ["MP3", "WAV", "AIFF", "FLAC"];

const CAPS: {
  Icon: React.ElementType;
  title: string;
  body: string;
}[] = [
  {
    Icon: PlayCircleOutlineIcon,
    title: "Upload & play",
    body: "Drag in audio up to 100 MB. Store it and play it back right here in your session.",
  },
  {
    Icon: LayersOutlinedIcon,
    title: "Projects & folders",
    body: "Organize your uploads into projects and folders — exactly like a full account.",
  },
  {
    Icon: LinkOutlinedIcon,
    title: "Share a link",
    body: "Generate a read-only public URL anyone can open to preview your track.",
  },
];

/** Small mono format badge row, mirrors `.formats`/`.fmt` from the design deck. */
const Formats: React.FC = () => (
  <div className="flex gap-[8px] mt-[2px]">
    {FORMATS.map((f) => (
      <span
        key={f}
        className="font-mono text-[11.5px] text-muted border border-border rounded-[6px] px-[9px] py-[4px] tracking-[0.04em]"
      >
        {f}
      </span>
    ))}
  </div>
);

/**
 * GuestHubHero — the guest landing state on the Hub (Tracks tab) when nothing
 * has been uploaded yet. Ports design-deck slide "Hub · Guest mode — G1"
 * (guest-flow.jsx `TryHeroV1` + `GuestHubView` banner) to Tailwind + tokens.
 *
 * Composes: an inline "exploring as a guest" banner, a drop hero that opens the
 * real batch-upload flow, and a "What you can do right now" capability grid.
 * Signup CTAs reuse the same Auth0 redirect as GuestShellBanner's "Save your work".
 */
const GuestHubHero: React.FC<GuestHubHeroProps> = ({ onBrowse, isDragging = false }) => {
  const { loginWithRedirect } = useAuth0();

  const onSignup = () => {
    loginWithRedirect({
      appState: { targetUrl: "/initial-setting" },
      authorizationParams: { screen_hint: "signup" },
    });
  };

  return (
    <div className="flex-1 flex flex-col gap-[18px] min-h-0 w-full font-sans">
      {/* Inline guest banner */}
      <div className="flex items-center gap-[12px] mb-[14px] rounded-[12px] px-[14px] py-[11px] border border-[rgba(39,182,203,0.28)] bg-[linear-gradient(90deg,rgba(39,182,203,0.12),rgba(39,182,203,0.03))]">
        <span className="w-[8px] h-[8px] flex-none rounded-full bg-cyan shadow-[0_0_0_4px_rgba(39,182,203,0.18)]" />
        <span className="flex-1 text-[13.5px] text-body">
          <b className="text-white font-semibold">You&apos;re exploring as a guest.</b>{" "}
          Anything you add stays in this session — up to 100&nbsp;MB.
        </span>
        <button
          type="button"
          onClick={onSignup}
          className="whitespace-nowrap font-sans text-[13.5px] font-semibold text-cyan cursor-pointer bg-transparent border-0 p-0"
        >
          Sign up to save →
        </button>
      </div>

      {/* Drop hero */}
      <div className="relative overflow-hidden flex flex-col items-center text-center rounded-[20px] border border-border p-[30px_34px_28px] shadow-[0_24px_60px_-34px_rgba(0,0,0,0.7)] bg-surface bg-[radial-gradient(720px_300px_at_50%_-8%,rgba(39,182,203,0.10),transparent_72%)]">
        <span className="inline-flex items-center gap-[8px] mb-[16px] rounded-full px-[13px] py-[6px] text-[12.5px] font-semibold tracking-[0.04em] text-cyan border border-[rgba(39,182,203,0.24)] bg-[rgba(39,182,203,0.10)]">
          <AutoAwesomeIcon sx={{ fontSize: 15 }} /> TRY IT FREE · NO SIGNUP
        </span>
        <h2 className="m-0 text-white text-[27px] font-bold tracking-[-0.015em]">
          Drop a track and start building
        </h2>
        <p className="text-muted text-[14.5px] leading-[1.55] max-w-[460px] mt-[11px]">
          Upload a mix, stem, or rough idea and explore the studio — play it back, sort it
          into projects, and share a link. No account needed to begin.
        </p>

        <div
          className={cn(
            "mt-[22px] w-full rounded-[16px] border p-[30px_24px] flex flex-col items-center gap-[14px]",
            "transition-[border-color,background] duration-[180ms]",
            isDragging
              ? "border-cyan bg-bg bg-[radial-gradient(480px_220px_at_50%_30%,rgba(39,182,203,0.10),transparent)]"
              : "border-transparent bg-bg hover:border-cyan hover:bg-[radial-gradient(480px_220px_at_50%_30%,rgba(39,182,203,0.10),transparent)]",
          )}
        >
          <span className="w-[72px] h-[72px] rounded-full grid place-items-center text-cyan bg-[rgba(39,182,203,0.13)]">
            <CloudUploadOutlinedIcon sx={{ fontSize: 36 }} />
          </span>
          <div className="text-white text-[18px] font-bold">
            {isDragging ? "Drop music here" : "Drag & drop music here"}
          </div>
          <div className="flex items-center gap-[13px] text-faint text-[12.5px] before:content-[''] before:w-[40px] before:h-px before:bg-border after:content-[''] after:w-[40px] after:h-px after:bg-border">
            or
          </div>
          <Button
            variant="primary"
            size="lg"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={onBrowse}
            disabled={!onBrowse}
          >
            Browse files
          </Button>
          <Formats />
        </div>
      </div>

      {/* Capability grid */}
      <div className="flex items-baseline justify-between px-[2px]">
        <h3 className="m-0 text-white text-[15px] font-bold">What you can do right now</h3>
        <span className="text-muted text-[13px]">No signup required</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-[14px]">
        {CAPS.map(({ Icon, title, body }) => (
          <button
            key={title}
            type="button"
            onClick={onBrowse}
            className="text-left font-sans flex flex-col gap-[9px] rounded-[14px] border border-border-soft bg-surface p-[16px_16px_15px] cursor-pointer transition-[border-color,transform,background] duration-150 hover:border-border hover:bg-surface-hi hover:-translate-y-[2px]"
          >
            <span className="w-[38px] h-[38px] rounded-[11px] grid place-items-center text-cyan bg-[rgba(39,182,203,0.12)]">
              <Icon sx={{ fontSize: 20 }} />
            </span>
            <b className="text-white text-[14px] font-semibold">{title}</b>
            <p className="m-0 flex-1 text-muted text-[12.5px] leading-[1.45]">{body}</p>
            <span className="inline-flex items-center gap-[5px] text-cyan text-[12px] font-semibold">
              Try it <ArrowForwardIcon sx={{ fontSize: 13 }} />
            </span>
          </button>
        ))}
        <button
          type="button"
          onClick={onSignup}
          className="text-left font-sans flex flex-col gap-[9px] rounded-[14px] p-[16px_16px_15px] cursor-pointer transition-[border-color,transform,background] duration-150 border border-[rgba(39,182,203,0.3)] bg-surface bg-[radial-gradient(150%_120%_at_0%_0%,rgba(39,182,203,0.12),transparent_62%)] hover:border-[rgba(39,182,203,0.5)] hover:-translate-y-[2px]"
        >
          <span className="w-[38px] h-[38px] rounded-[11px] grid place-items-center text-cyan bg-[rgba(39,182,203,0.16)]">
            <LockOutlinedIcon sx={{ fontSize: 20 }} />
          </span>
          <b className="text-white text-[14px] font-semibold">Unlock 5&nbsp;GB free</b>
          <p className="m-0 flex-1 text-muted text-[12.5px] leading-[1.45]">
            Sign up to save your work, go private, and invite collaborators.
          </p>
          <span className="inline-flex items-center gap-[5px] text-cyan text-[12px] font-semibold">
            Sign up <ArrowForwardIcon sx={{ fontSize: 13 }} />
          </span>
        </button>
      </div>
    </div>
  );
};

export default GuestHubHero;
